import { useTranslation } from "react-i18next";
import LeafTag from "../../../../common/leaf/LeafTag/LeafTag";
import { WatchlistToggle } from "../../../ActionReports/common/WatchlistToggle/WatchlistToggle";
import "./BasicInfo.scss";
import defaultContent from "../../../../content/downlineProfile";
import awardLabelContent from "../../../../content/awardLabels";
import commonContent from "../../../../content/common";
import { IconChevronRight } from "../../../../common/icons/IconChevronRight/IconChevronRight";
import { IconOnSop } from "../../../../common/icons/IconOnSop/IconOnSop";
import { IconAutoRenewal } from "../../../../common/icons/IconAutoRenewal/IconAutoRenewal";
import { IconNotes } from "../../../../common/icons/IconNotes/IconNotes";
import LeafRectangleLoader from "../../../../common/leaf/LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import { ActionReportRow } from "../../../ActionReports/common/ActionReportListView/types";
import { useWatchlistFetch } from "../../../ActionReports/hooks/useWatchlistFetch";
import { useWatchlistToggle } from "../../../ActionReports/hooks/useWatchlistToggle";
import { useNavigate } from "react-router-dom";
import { ROUTE_PATHS } from "../../../../common/enums/routing-enums";
import { useAppDispatch, useAppSelector } from "../../../../store";
import { hideDownlineProfile, hideProfile } from "../../../../reducers/profile";
import { useEffect, useState } from "react";
import LeafToastMessage from "../../../../common/leaf/LeafToastMessage/LeafToastMessage";
import { createPortal } from "react-dom";
import useWindowSize from "../../../../common/hooks/useWindowSize";
import LeafPinLevelName from "../../../../common/leaf/LeafPinLevelName/LeafPinLevelName";
import { useWrappedSeparators } from "../../../../common/hooks/useWrappedSeparators";
import { getCurrentPeriod, getPerformanceYearShort, getPrevYearShort } from "../../../../common/util/period";
import { trackViewTheirLos } from "../../PlatinumProfile/downlineProfileTealium";
import { EVENTS } from "../../../../common/enums/events";
import maskAboNumber from "../../../../common/util/maskAboNumber";

export interface AboBasicInfoProps {
  aboInfo?: {
    displayName?: string;
    affNo?: string;
    aboNo?: string;
  };
  tags?: {
    isOnAutoRenewal?: boolean;
    hasNotes?: boolean;
    isOnSop?: boolean;
  };
  award?: {
    highestAward?: number;
    highestAwardDate?: string;
    currentAward?: number;
    currentAwardDate?: number;
    isPlatinumOrAbove: boolean;
    isEmeraldAndAbove?: boolean;
    isDiamondAndAbove?: boolean;
  };
  isLoading?: boolean;
  isConfidential?: boolean;
  isRootAbo?: boolean;
  isFromEdLos?: boolean;
  edlosAboMeta?: { level?: number; enableDownlineLOS?: boolean } | null;
}

export type CustomerBasicInfoProps = Pick<AboBasicInfoProps, "aboInfo" | "tags" | "isLoading" | "isConfidential">;

const BasicInfoSkeleton = ({ type }: { type: "abo" | "customer" }) => {
  return (
    <div className="BasicInfo">
      <div className="BasicInfo--nameAndSave">
        <LeafRectangleLoader isLoading height="2.25rem" width="14rem" margin="0" />
        {type === "abo" && (
          <div
            className="rectangle-loader basic-info-wl-loader"
            style={{ width: "2.25rem", height: "2.25rem", borderRadius: "50%", marginBottom: 0 }}
          />
        )}
      </div>
      <div className="BasicInfo--numberAndAwardLevel">
        <LeafRectangleLoader isLoading height="1rem" width="5rem" margin="0" />
        {type === "abo" && (
          <>
            <LeafRectangleLoader isLoading height="1rem" width="9.75rem" margin="0" />
            <LeafRectangleLoader isLoading height="1rem" width="9.75rem" margin="0" />
          </>
        )}
      </div>

      <div className="BasicInfo--additionalPill">
        <LeafRectangleLoader isLoading height="1.5rem" width="5.625rem" margin="0" />
        <LeafRectangleLoader isLoading height="1.5rem" width="9.75rem" margin="0" />
        <LeafRectangleLoader isLoading height="1.5rem" width="4.5625rem" margin="0" />
      </div>
    </div>
  );
};

const BasicInfoTags = ({ tags }: Pick<CustomerBasicInfoProps, "tags">) => {
  const { t } = useTranslation(["downlineProfile", "common"]);

  return (
    <div className="BasicInfo--additionalPill">
      {tags?.isOnSop && (
        <LeafTag variant="solidGreen">
          <span>
            <IconOnSop />
            <p>{t("onSop", defaultContent.onSop)}</p>
          </span>
        </LeafTag>
      )}
      {tags?.isOnAutoRenewal && (
        <LeafTag variant="solidGreen">
          <span>
            <IconAutoRenewal />
            <p>{t("onAutoRenewal", defaultContent.onAutoRenewal)}</p>
          </span>
        </LeafTag>
      )}
      {tags?.hasNotes && (
        <LeafTag variant="darkGray">
          <span>
            <IconNotes />
            <p>{t("notesTag", defaultContent.notesTag)}</p>
          </span>
        </LeafTag>
      )}
    </div>
  );
};

export const AboBasicInfo = ({
  aboInfo,
  tags,
  award,
  isLoading,
  isConfidential,
  isRootAbo,
  isFromEdLos,
  edlosAboMeta,
}: AboBasicInfoProps) => {
  const { t } = useTranslation(["downlineProfile", "common"]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { width } = useWindowSize();
  const { isImpersonation, configuration } = useAppSelector((s) => s.boot);
  const { refetch } = useWatchlistFetch();
  const { isInWatchlist, toggleWatchlist } = useWatchlistToggle({
    row: { aboNumber: aboInfo?.aboNo ?? "", aff: aboInfo?.affNo ?? "" } as ActionReportRow,
  });
  const [toastState, setToastState] = useState<"success" | "error">("success");
  const [showToast, setShowToast] = useState(false);
  const [wasInWatchlist, setWasInWatchlist] = useState(false);
  const [toastCounter, setToastCounter] = useState(0);
  const [portalTarget, setPortalTarget] = useState<Element | null>(null);
  const previousPY = getPrevYearShort(getCurrentPeriod());

  const isMaskAboNumber = useAppSelector((s) => s.boot.configuration.profile.maskConfidentialAboNumber);

  // Hide each "|" separator only when the item that follows it has wrapped to a new line, so a
  // separator never dangles at the end of a line. Indexed by separator position (0 = after #aboNo).
  const { rowRef: numberRowRef, wrappedSeparators } = useWrappedSeparators<HTMLDivElement>([
    aboInfo?.aboNo,
    award?.highestAward,
    isConfidential,
  ]);

  useEffect(() => {
    setPortalTarget(document.querySelector("#contact-actions"));
  }, [width, isLoading]);
  const portalFlag = portalTarget !== null;

  const handleWatchlistToggle = async () => {
    setShowToast(false);
    setWasInWatchlist(isInWatchlist);
    try {
      await toggleWatchlist();
      if (!isImpersonation) refetch();
      setToastState("success");
    } catch {
      setToastState("error");
    } finally {
      setToastCounter((previousCounter) => previousCounter + 1);
      setShowToast(true);
    }
  };

  if (isLoading) return <BasicInfoSkeleton type="abo" />;

  return (
    <>
      <LeafToastMessage
        showToast={showToast}
        theme={toastState}
        className="basicInfo__watchlist-toast"
        toastId={`profile-watchlist-notification-${toastCounter}`}
        htmlContent={
          toastState === "success" ? (
            <span>
              {t(wasInWatchlist ? "watchlist.removed" : "watchlist.added", {
                name: aboInfo?.displayName,
                defaultValue: wasInWatchlist ? defaultContent.watchlist.removed : defaultContent.watchlist.added,
              })}
            </span>
          ) : (
            <span>{t("watchlist.error", defaultContent.watchlist.error)}</span>
          )
        }
      />
      <div className="BasicInfo">
        <div className="BasicInfo--nameAndSave">
          <p>
            {!isConfidential
              ? (aboInfo?.displayName ?? "")
              : `${t("confidentialUser", commonContent.confidentialUser, { ns: "common" })}`}
          </p>
          {!isRootAbo && !isFromEdLos && (
            <span>
              {Number(width) >= 1024 ? (
                <WatchlistToggle watchlist={isInWatchlist} watchlistToggle={() => handleWatchlistToggle()} />
              ) : (
                portalFlag &&
                portalTarget &&
                createPortal(
                  <WatchlistToggle watchlist={isInWatchlist} watchlistToggle={() => handleWatchlistToggle()} />,
                  portalTarget,
                )
              )}
            </span>
          )}
        </div>
        <div className="BasicInfo--numberAndAwardLevel" ref={numberRowRef}>
          <p>#{isConfidential && isMaskAboNumber ? maskAboNumber(aboInfo?.aboNo ?? "") : aboInfo?.aboNo}</p>
          <span
            className={`BasicInfo--separator${wrappedSeparators[0] ? " BasicInfo--separator--hidden" : ""}`}
            aria-hidden="true"
          >
            |
          </span>
          <p>
            {award?.currentAward ? (
              <>
                {`${t("keyInformation.awardLabels.currentAward", defaultContent.keyInformation.awardLabels.currentAward, { date: getPerformanceYearShort(award.currentAwardDate ?? getCurrentPeriod()) })}: `}
              </>
            ) : (
              <>{`${t("highestAward", defaultContent.highestAward, { py_2_digit: previousPY })}: `}</>
            )}
            <span className="BasicInfo__highestAwardPin">
              {award?.isDiamondAndAbove ? (
                t("awardLabels.diamond-and-above", awardLabelContent.awardLabels["diamond-and-above"], {
                  ns: "awardLabels",
                })
              ) : award?.isEmeraldAndAbove ? (
                t("awardLabels.emerald-and-above", awardLabelContent.awardLabels["emerald-and-above"], {
                  ns: "awardLabels",
                })
              ) : award?.isPlatinumOrAbove ? (
                t("platinumOrAbove", defaultContent.platinumOrAbove)
              ) : award?.currentAward ? (
                <LeafPinLevelName pinLevelCode={Number(award?.currentAward)} />
              ) : (
                <LeafPinLevelName pinLevelCode={Number(award?.highestAward)} />
              )}
            </span>
          </p>
          {!isRootAbo && (!isFromEdLos || edlosAboMeta?.enableDownlineLOS) && configuration.navigation.showLOS && (
            <>
              <span
                className={`BasicInfo--separator${wrappedSeparators[1] ? " BasicInfo--separator--hidden" : ""}`}
                aria-hidden="true"
              >
                |
              </span>
              <button
                className="BasicInfo__viewLosButton"
                onClick={() => {
                  dispatch(hideDownlineProfile());
                  dispatch(hideProfile());
                  if (isFromEdLos) {
                    window.dispatchEvent(
                      new CustomEvent(EVENTS.EVENT_LISTENER.EDLOS_VIEW_DOWNLINE_LOS_FROM_PROFILE, {
                        detail: {
                          level: edlosAboMeta?.level,
                          name: aboInfo?.displayName,
                          aff: aboInfo?.affNo,
                          abo: aboInfo?.aboNo,
                        },
                      }),
                    );
                  } else {
                    navigate(ROUTE_PATHS.LOS, { state: { targetAbo: aboInfo?.aboNo } });
                  }
                  trackViewTheirLos();
                }}
              >
                <p>{t("viewTheirLOS", defaultContent.viewTheirLOS)}</p>
                <IconChevronRight />
              </button>
            </>
          )}
        </div>
        <BasicInfoTags tags={tags} />
      </div>
    </>
  );
};

export const CustomerBasicInfo = ({ aboInfo, tags, isLoading, isConfidential }: CustomerBasicInfoProps) => {
  const { t } = useTranslation(["downlineProfile", "common"]);
  const isMaskAboNumber = useAppSelector((s) => s.boot.configuration.profile.maskConfidentialAboNumber);

  if (isLoading) return <BasicInfoSkeleton type="customer" />;

  return (
    <div className="BasicInfo">
      <div className="BasicInfo--nameAndSave">
        <p>
          {!isConfidential
            ? (aboInfo?.displayName ?? "")
            : `${t("confidentialUser", commonContent.confidentialUser, { ns: "common" })}`}
        </p>
      </div>
      <div className="BasicInfo--numberAndAwardLevel">
        <p>#{isConfidential && isMaskAboNumber ? maskAboNumber(aboInfo?.aboNo ?? "") : aboInfo?.aboNo}</p>
      </div>
      <BasicInfoTags tags={tags} />
    </div>
  );
};
